/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    TablePagination,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Autocomplete,
    CircularProgress
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { Shareholder, Cusip, Ftp, CreateShareholderData } from '../types/api.types';
import { shareholderService } from '../services/api/shareholderService';
import { cusipService } from '../services/api/cusipService';
import { ftpService } from '../services/api/ftpService';
import { useApiState } from '../context/ApiStateContext';
import LoadingOverlay from '../components/LoadingOverlay';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusPopup from '../components/StatusPopup';

const CUSIP_TO_ISSUER: Record<string, string> = {
    '037833100': 'Apple',
    '88160R101': 'Tesla',
    '67066G104': 'NVIDIA',
    '00214Q104': 'iShares',
    '46090E103': 'Invesco',
    '46090A689': 'Invesco',
    '46090A697': 'Invesco',
    '46090A879': 'Invesco',
    '46138G888': 'Invesco',
    '46137V399': 'Invesco',
    '46137V563': 'Invesco',
    '46137V621': 'Invesco',
    '46138E511': 'Invesco',
    '46138E537': 'Invesco',
    '46138E206': 'Invesco',
    '46138E354': 'Invesco',
    '46138E610': 'Invesco',
    '46138E594': 'Invesco',
    '46138E719': 'Invesco',
    '46138G870': 'Invesco',
    '46138E131': 'Invesco',
    '46138E404': 'Invesco',
};

const ISSUER_OPTIONS = ['Apple', 'Tesla', 'NVIDIA', 'Invesco', 'iShares'];

const getIssuerName = (cusip: string): string => {
    const normalized = (cusip || '').trim();
    return CUSIP_TO_ISSUER[normalized] ?? (normalized || '—');
};

const ShareholderPage: React.FC = () => {
    const [shareholders, setShareholders] = useState<Shareholder[]>([]);
    const [cusips, setCusips] = useState<Cusip[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingShareholder, setEditingShareholder] = useState<Shareholder | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [issuerFilter, setIssuerFilter] = useState<string>('');
    const { loading, setLoading, setError, setSuccess } = useApiState();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [shareholderToDelete, setShareholderToDelete] = useState<string | null>(null);
    const [formData, setFormData] = useState<Partial<Shareholder>>({});
    const isFirstRender = useRef(true);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [ftps, setFtps] = useState<Ftp[]>([]);
    const [selectedFtp, setSelectedFtp] = useState<Ftp | null>(null);
    const [uploadProgress, setUploadProgress] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportFormat, setExportFormat] = useState<'csv' | 'text'>('csv');
    const [statusPopup, setStatusPopup] = useState<{
        open: boolean;
        success: boolean;
        message: string;
    }>({
        open: false,
        success: false,
        message: ''
    });
    const [uploadFileType, setUploadFileType] = useState<'csv' | 'text'>('csv');

    const fetchShareholders = async (
        currentPage: number = 0,
        currentPageSize: number = 10,
        search: string = '',
        issuer: string = ''
    ) => {
        setLoading(true);
        try {
            const response = await shareholderService.getAllShareholders(
                search.trim(),
                currentPage + 1,
                currentPageSize,
                issuer || undefined
            );
            setShareholders(response.data);
            setTotal(response.total);
            setSuccess(true);
        } catch (error) {
            setError(error as any);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            fetchShareholders(0, 10);
            isFirstRender.current = false;
            return;
        }

        if (page > 0 || rowsPerPage !== 10) {
            fetchShareholders(page, rowsPerPage, searchQuery, issuerFilter);
        }
    }, [page, rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // const handleAdd = () => {
    //     setEditingShareholder(null);
    //     setFormData({});
    //     fetchCusips(); // Fetch CUSIPs when adding
    //     setDialogOpen(true);
    // };

    const handleEdit = async (shareholder: Shareholder) => {
        setLoading(true);
        try {
            const updatedShareholder = await shareholderService.getShareholderById(shareholder.id);
            setEditingShareholder(updatedShareholder);
            fetchCusips(); // Fetch CUSIPs when editing
            setDialogOpen(true);
        } catch (error) {
            setError(error as any);
        }
        setLoading(false);
    };

    const handleDeleteClick = (id: string) => {
        setShareholderToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!shareholderToDelete) return;

        setLoading(true);
        try {
            await shareholderService.deleteShareholder(shareholderToDelete);
            showStatusPopup(true, 'Shareholder deleted successfully!');
            const response = await shareholderService.getAllShareholders('', page + 1, rowsPerPage, issuerFilter || undefined);
            setShareholders(response.data);
            setTotal(response.total);
        } catch (error) {
            showStatusPopup(false, (error as any)?.message || 'Delete operation failed');
        }
        setLoading(false);
        setDeleteDialogOpen(false);
        setShareholderToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setShareholderToDelete(null);
    };

    const handleExportClick = () => {
        setExportDialogOpen(true);
    };

    const handleExportConfirm = async () => {
        setLoading(true);
        try {
            const blob = exportFormat === 'csv'
                ? await shareholderService.exportToCsv()
                : await shareholderService.exportToText();

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const guid = crypto.randomUUID();
            const date = new Date().toISOString().split('T')[0];
            const extension = exportFormat === 'csv' ? 'csv' : 'txt';
            a.download = `shareholder-${date}-${guid}.${extension}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            showStatusPopup(true, `Export to ${exportFormat.toUpperCase()} completed successfully!`);
            setExportDialogOpen(false);
        } catch (error) {
            showStatusPopup(false, (error as any)?.message || 'Export failed');
        }
        setLoading(false);
    };

    const handleExportCancel = () => {
        setExportDialogOpen(false);
        setExportFormat('csv');
    };

    const showStatusPopup = (success: boolean, message: string) => {
        setStatusPopup({
            open: true,
            success,
            message
        });
    };

    const handleStatusPopupClose = () => {
        setStatusPopup(prev => ({ ...prev, open: false }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        const formDataObj = new FormData(event.currentTarget);
        const values: CreateShareholderData = {
            cusip: formData.cusip || editingShareholder?.cusip || '',
            accountNumber: formDataObj.get('accountNumber') as string,
            email: formDataObj.get('email') as string,
            firstName: formDataObj.get('firstName') as string,
            lastName: formDataObj.get('lastName') as string,
            numberOfShares: parseInt(formDataObj.get('numberOfShares') as string),
            recordDate: formDataObj.get('recordDate') as string,
            addressLine1: formDataObj.get('addressLine1') as string,
            addressLine2: (formDataObj.get('addressLine2') as string) || null,
            city: formDataObj.get('city') as string,
            state: formDataObj.get('state') as string,
            zipCode: formDataObj.get('zipCode') as string,
            country: formDataObj.get('country') as string,
            controlNo: formDataObj.get('controlNo') as string,
        };

        if (!values.cusip) {
            showStatusPopup(false, 'CUSIP is required');
            setLoading(false);
            return;
        }

        try {
            if (editingShareholder) {
                await shareholderService.updateShareholder(editingShareholder.id, values);
                showStatusPopup(true, 'Shareholder updated successfully!');
            } else {
                await shareholderService.createShareholder(values);
                showStatusPopup(true, 'Shareholder created successfully!');
            }
            setDialogOpen(false);
            setFormData({}); // Reset form data
            const response = await shareholderService.getAllShareholders('', page + 1, rowsPerPage, issuerFilter || undefined);
            setShareholders(response.data);
            setTotal(response.total);
        } catch (error) {
            showStatusPopup(false, (error as any)?.message || 'Operation failed');
        }
        setLoading(false);
    };

    const handleSearch = () => {
        setPage(0); // Reset to first page when searching/filtering
        fetchShareholders(0, rowsPerPage, searchQuery, issuerFilter);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const fetchCusips = async () => {
        try {
            const response = await cusipService.getAllCusips('', 1, 1000);
            setCusips(response.data);
        } catch (error) {
            setError(error as any);
            console.error('Error fetching CUSIPs:', error);
        }
    };

    const handleUploadClick = async () => {
        setLoading(true);
        try {
            const response = await ftpService.getAllFtps('', 1, 1000);
            setFtps(response.data);
            setUploadDialogOpen(true);
        } catch (error) {
            setError(error as any);
        }
        setLoading(false);
    };

    const handleUploadConfirm = async () => {
        if (!selectedFtp) {
            showStatusPopup(false, 'Please select an FTP server');
            return;
        }

        setUploadProgress(true);
        try {
            const blob = uploadFileType === 'csv'
                ? await shareholderService.exportToCsv()
                : await shareholderService.exportToText();
            const guid = crypto.randomUUID();
            const date = new Date().toISOString().split('T')[0];
            const extension = uploadFileType === 'csv' ? 'csv' : 'txt';
            const filename = `shareholder-${date}-${guid}.${extension}`;
            await ftpService.uploadFile(selectedFtp.id!, blob, filename);
            showStatusPopup(true, 'File uploaded to FTP successfully!');
            setUploadDialogOpen(false);
            setSelectedFtp(null);
        } catch (error) {
            showStatusPopup(false, (error as any)?.message || 'Upload failed');
        }
        setUploadProgress(false);
    };

    const handleUploadCancel = () => {
        setUploadDialogOpen(false);
        setSelectedFtp(null);
    };

    return (
        <Box sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minHeight: 'calc(100vh - 120px)'
        }}>
            <Box sx={{ p: 0 }}>
                <LoadingOverlay open={loading} />
                <StatusPopup
                    open={statusPopup.open}
                    success={statusPopup.success}
                    message={statusPopup.message}
                    onClose={handleStatusPopupClose}
                />

                {/* Header Section */}
                <Box sx={{
                    mb: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Shareholder Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Manage shareholder data and export to various formats
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleUploadClick}
                            startIcon={<CloudUploadIcon />}
                            sx={{ borderRadius: 2 }}
                            disabled
                        >
                            Upload to FTP
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleExportClick}
                            sx={{ borderRadius: 2 }}
                        >
                            Export
                        </Button>
                        {/* <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAdd}
                            sx={{
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                            }}
                        >
                            Add Shareholder
                        </Button> */}
                    </Box>
                </Box>

                {/* Search Section */}
                <Box sx={{
                    mb: 3,
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <TextField
                        size="small"
                        placeholder="Search shareholders..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={{
                            minWidth: 300,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="issuer-filter-label">Issuer Name</InputLabel>
                        <Select
                            labelId="issuer-filter-label"
                            value={issuerFilter}
                            label="Issuer Name"
                            onChange={(e) => setIssuerFilter(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {ISSUER_OPTIONS.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading}
                        sx={{ borderRadius: 2 }}
                    >
                        Search
                    </Button>
                </Box>

                {/* Table Section */}
                <Box sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Account Number</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>CUSIP</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Issuer Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: '180px', minWidth: '180px' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Shares</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: '200px', minWidth: '200px' }}>Address</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: '140px', minWidth: '140px' }}>Share Purchase Date</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shareholders.map((shareholder) => (
                                    <TableRow
                                        key={shareholder.id}
                                        sx={{
                                            '&:hover': { bgcolor: '#f8f9fa' },
                                            '&:nth-of-type(even)': { bgcolor: '#fafafa' }
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 500 }}>{shareholder.accountNumber}</TableCell>
                                        <TableCell>
                                            <Box sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                bgcolor: '#e8f5e8',
                                                color: '#2e7d32',
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                            }}>
                                                {shareholder.cusip}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{getIssuerName(shareholder.cusip)}</TableCell>
                                        <TableCell sx={{ fontWeight: 500, width: '180px', minWidth: '180px' }}>{shareholder.fullName}</TableCell>
                                        <TableCell>{shareholder.email}</TableCell>
                                        <TableCell>
                                            <Box sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                bgcolor: '#fff3e0',
                                                color: '#f57c00',
                                                fontSize: '0.875rem',
                                                fontWeight: 600
                                            }}>
                                                {shareholder.numberOfShares.toLocaleString()}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '200px', minWidth: '200px' }}>
                                            <Typography variant="body2">
                                                {shareholder.addressLine1}
                                                {shareholder.addressLine2 && `, ${shareholder.addressLine2}`}
                                                <br />
                                                {shareholder.city}, {shareholder.state} {shareholder.zipCode}
                                                <br />
                                                {shareholder.country}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ minWidth: '220px' }}>{new Date(shareholder.recordDate).toLocaleDateString()}</TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <IconButton
                                                    onClick={() => handleEdit(shareholder)}
                                                    color="primary"
                                                    sx={{
                                                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' }
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleDeleteClick(shareholder.id)}
                                                    color="error"
                                                    sx={{
                                                        '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            borderTop: '1px solid #e0e0e0',
                            bgcolor: '#f8f9fa'
                        }}
                    />
                </Box>

                {/* Add/Edit Dialog */}
                <Dialog
                    open={dialogOpen}
                    onClose={() => {
                        setDialogOpen(false);
                        setFormData({}); // Reset form data when dialog closes
                    }}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 2 }
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        <DialogTitle sx={{
                            borderBottom: '1px solid #e0e0e0',
                            bgcolor: '#f8f9fa'
                        }}>
                            {editingShareholder ? 'Edit Shareholder' : 'Add Shareholder'}
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        options={cusips}
                                        getOptionLabel={(option) => `${option.cusip} - ${option.description || 'No description'}`}
                                        value={cusips.find(c => c.cusip === (formData.cusip || editingShareholder?.cusip)) || null}
                                        onChange={(event, newValue) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                cusip: newValue?.cusip || ''
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="CUSIP"
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 1.5
                                                    }
                                                }}
                                            />
                                        )}
                                        filterOptions={(options, { inputValue }) => {
                                            const searchTerm = inputValue.toLowerCase();
                                            return options.filter(option =>
                                                option.cusip.toLowerCase().includes(searchTerm) ||
                                                (option.description?.toLowerCase() || '').includes(searchTerm)
                                            );
                                        }}
                                    />
                                </FormControl>
                                <TextField
                                    name="accountNumber"
                                    label="Account Number"
                                    type="text"
                                    fullWidth
                                    required
                                    defaultValue={editingShareholder?.accountNumber}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                                <TextField
                                    name="email"
                                    label="Email"
                                    type="email"
                                    fullWidth
                                    required
                                    defaultValue={editingShareholder?.email}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        name="firstName"
                                        label="First Name"
                                        type="text"
                                        fullWidth
                                        required
                                        defaultValue={editingShareholder?.firstName}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                    <TextField
                                        name="lastName"
                                        label="Last Name"
                                        type="text"
                                        fullWidth
                                        required
                                        defaultValue={editingShareholder?.lastName}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                </Box>
                                <TextField
                                    name="numberOfShares"
                                    label="Number of Shares"
                                    type="number"
                                    fullWidth
                                    required
                                    defaultValue={editingShareholder?.numberOfShares}
                                    inputProps={{ min: 0 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                                <TextField
                                    name="recordDate"
                                    label="Record Date"
                                    type="date"
                                    fullWidth
                                    defaultValue={editingShareholder?.recordDate ? new Date(editingShareholder.recordDate).toISOString().split('T')[0] : ''}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        name="addressLine1"
                                        label="Address Line 1"
                                        type="text"
                                        fullWidth
                                        required
                                        defaultValue={editingShareholder?.addressLine1}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                    <TextField
                                        name="addressLine2"
                                        label="Address Line 2"
                                        type="text"
                                        fullWidth
                                        defaultValue={editingShareholder?.addressLine2 || ''}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        name="city"
                                        label="City"
                                        type="text"
                                        fullWidth
                                        required
                                        defaultValue={editingShareholder?.city}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                    <TextField
                                        name="state"
                                        label="State"
                                        type="text"
                                        fullWidth
                                        required
                                        defaultValue={editingShareholder?.state}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        name="zipCode"
                                        label="ZIP Code"
                                        type="text"
                                        fullWidth
                                        required
                                        defaultValue={editingShareholder?.zipCode}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                    <TextField
                                        name="country"
                                        label="Country"
                                        type="text"
                                        fullWidth
                                        required
                                        defaultValue={editingShareholder?.country}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                </Box>
                                <TextField
                                    name="controlNo"
                                    label="Control Number"
                                    type="text"
                                    fullWidth
                                    required
                                    defaultValue={editingShareholder?.controlNo || ''}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, gap: 2 }}>
                            <Button
                                onClick={() => setDialogOpen(false)}
                                variant="outlined"
                                sx={{ borderRadius: 2 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                                }}
                            >
                                {editingShareholder ? 'Update' : 'Create'}
                            </Button>
                        </DialogActions>
                    </form>
                </Dialog>

                <ConfirmDialog
                    open={deleteDialogOpen}
                    title="Delete Shareholder"
                    message={`Are you sure you want to delete this shareholder?`}
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />

                {/* Upload to FTP Dialog */}
                <Dialog
                    open={uploadDialogOpen}
                    onClose={handleUploadCancel}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 2, width: 500 }
                    }}
                >
                    <DialogTitle sx={{
                        borderBottom: '1px solid #e0e0e0',
                        bgcolor: '#f8f9fa'
                    }}>
                        Upload to FTP Server
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="upload-file-type-label">File Type</InputLabel>
                            <Select
                                labelId="upload-file-type-label"
                                value={uploadFileType}
                                label="File Type"
                                onChange={e => setUploadFileType(e.target.value as 'csv' | 'text')}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                            >
                                <MenuItem value="csv">CSV</MenuItem>
                                <MenuItem value="text">Text</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <Autocomplete
                                options={ftps}
                                getOptionLabel={(option) => `${option.protocol} - ${option.host}:${option.port}`}
                                value={selectedFtp}
                                onChange={(event, newValue) => {
                                    setSelectedFtp(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select FTP Server"
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, gap: 2 }}>
                        <Button
                            onClick={handleUploadCancel}
                            variant="outlined"
                            sx={{ borderRadius: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUploadConfirm}
                            variant="contained"
                            color="primary"
                            disabled={!selectedFtp || uploadProgress}
                            startIcon={uploadProgress ? <CircularProgress size={20} /> : null}
                            sx={{
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                            }}
                        >
                            {uploadProgress ? 'Uploading...' : 'Upload'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Export Dialog */}
                <Dialog
                    open={exportDialogOpen}
                    onClose={handleExportCancel}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 2 }
                    }}
                >
                    <DialogTitle sx={{
                        borderBottom: '1px solid #e0e0e0',
                        bgcolor: '#f8f9fa'
                    }}>
                        Export Shareholders
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel>Export Format</InputLabel>
                            <Select
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value as 'csv' | 'text')}
                                label="Export Format"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5
                                    }
                                }}
                            >
                                <MenuItem value="csv">Export to CSV</MenuItem>
                                <MenuItem value="text">Export to Text File</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, gap: 2 }}>
                        <Button
                            onClick={handleExportCancel}
                            variant="outlined"
                            sx={{ borderRadius: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleExportConfirm}
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            sx={{
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                            }}
                        >
                            Export
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default ShareholderPage; 